import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RESIDENT_RELATION_LABELS,
  type Page,
  type Resident,
  type ResidentRelation,
  type ResidentsOverview,
  type Unit,
} from '@azko/shared';
import { ApiRequestError, api } from '../lib/api.js';
import { formatPhone, maskPhone, phoneToE164 } from '../lib/format.js';
import { useSession } from '../lib/session.js';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  LoadingRows,
  MaskedInput,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  StatCard,
  StatusBadge,
  TextInput,
  cx,
  resetting,
  useCursorPages,
} from '../components/ui.js';
import { ImportResidentsCard } from '../components/ImportResidents.js';
import { ResidentUnitsEditor } from '../components/ResidentUnits.js';
import { useBlocks } from '../lib/useBlocks.js';
import { useDebouncedValue } from '../lib/useDebouncedValue.js';

/**
 * "101" sozinho nao identifica ninguem num condominio onde todo bloco tem um
 * 101. O bloco vem junto sempre que existir.
 */
function describeUnits(resident: Resident): string {
  if (resident.units.length === 0) return '—';
  return resident.units
    .map((unit) => (unit.block ? `${unit.block} • ${unit.unitLabel}` : unit.unitLabel))
    .join(', ');
}

/**
 * Como o morador e alcancado — ou o aviso de que ele nao e.
 *
 * Um travessao no lugar do telefone se le como "ninguem preencheu ainda", e nao
 * como "este morador nao vai ser avisado de encomenda nenhuma", que e o que ele
 * realmente significa. A lista da administradora chega assim em massa: na
 * primeira importacao real foram 115 moradores sem telefone de 478. Enquanto
 * ninguem vir isso na tela, o sintoma so aparece quando o morador reclama que a
 * encomenda ficou uma semana na portaria.
 *
 * O e-mail nao entra aqui: ele tem coluna propria. Ficar so como consolo de
 * quem nao tem telefone o escondia justamente de quem tem os dois — e a lista
 * de contato do predio nao pode depender de o morador estar incompleto para
 * mostrar o contato dele.
 */
function Contact({ resident }: { resident: Resident }) {
  if (resident.phoneE164) return <>{formatPhone(resident.phoneE164)}</>;
  return <StatusBadge tone="warn">Sem WhatsApp</StatusBadge>;
}

/**
 * E-mail do morador.
 *
 * Clicavel porque e para isso que ele esta na tela: quando o WhatsApp falta ou
 * o numero esta errado, o e-mail e o unico caminho que a portaria tem para
 * cobrar o cadastro sem passar pela administradora.
 */
function Email({ resident }: { resident: Resident }) {
  if (!resident.email) return <span className="text-faint">—</span>;

  return (
    <a
      href={`mailto:${resident.email}`}
      className="truncate underline decoration-line underline-offset-4 transition hover:text-accent"
    >
      {resident.email}
    </a>
  );
}

/**
 * LGPD, na tela.
 *
 * Duas obrigacoes que existiam na lei e nao no produto: o titular pode pedir
 * tudo o que guardamos sobre ele (art. 18, II e V) e pode pedir que apaguemos
 * (art. 18, VI). Enquanto nao havia rota, atender significava alguem abrir o
 * MySQL e montar a mao — ou seja, nao atender.
 *
 * Apagar NAO e excluir o registro: o historico de encomendas e registro
 * operacional do condominio e continua existindo, apontando para um morador sem
 * nome. Por isso o aviso fala em "dados pessoais", e nao em "morador" — e o que
 * de fato acontece, e a diferenca importa para quem clica.
 */
function PrivacyActions({ resident }: { resident: Resident }) {
  const { allows } = useSession();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canErase = allows('condominio:moradores:anonimizar');

  const exportData = useMutation({
    mutationFn: () => api.get<Record<string, unknown>>(`/residents/${resident.id}/dados`),
    onSuccess: (data) => {
      setError(null);
      // Monta o arquivo aqui em vez de apontar um link para a rota: assim o
      // download usa a mesma sessao e o mesmo tratamento de erro do resto do
      // app, sem depender de o cookie viajar numa navegacao solta.
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `morador-${resident.name.replace(/\s+/g, '-').toLowerCase()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Nao foi possivel exportar.'),
  });

  const erase = useMutation({
    mutationFn: () => api.delete<{ packagesKept: number }>(`/residents/${resident.id}/dados`),
    onSuccess: () => {
      setError(null);
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ['residents'] });
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Nao foi possivel apagar os dados.'),
  });

  return (
    <div className="mt-3 rounded-2xl border border-line bg-surface-2 p-4">
      <p className="text-sm font-bold">Dados pessoais (LGPD)</p>
      <p className="mt-0.5 text-sm text-muted">
        O morador pode pedir uma copia do que guardamos, ou pedir que apaguemos.
      </p>

      {error && (
        <div className="mt-3">
          <Alert>{error}</Alert>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-3">
        <Button
          variant="quiet"
          className="py-1.5"
          loading={exportData.isPending}
          onClick={() => exportData.mutate()}
        >
          Exportar copia
        </Button>

        {canErase &&
          (confirming ? (
            <>
              <Button
                variant="quiet"
                className="py-1.5 text-danger"
                loading={erase.isPending}
                onClick={() => erase.mutate()}
              >
                Confirmar: apagar dados
              </Button>
              <Button variant="quiet" className="py-1.5" onClick={() => setConfirming(false)}>
                Cancelar
              </Button>
            </>
          ) : (
            // Dois passos, e nao um `confirm()` do navegador: a segunda pergunta
            // precisa dizer o que sobra depois, e o dialogo nativo nao diz.
            <Button variant="quiet" className="py-1.5" onClick={() => setConfirming(true)}>
              Apagar dados pessoais
            </Button>
          ))}
      </div>

      {confirming && (
        <p className="mt-3 text-sm text-warn">
          O nome, telefone, e-mail e documento sao apagados e o vinculo com a unidade e desfeito.
          O historico de encomendas do condominio permanece, sem identificar a pessoa. Nao tem
          volta.
        </p>
      )}
    </div>
  );
}

export function ResidentsPage() {
  const { allows } = useSession();
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data: overview } = useQuery({
    queryKey: ['residents', 'overview'],
    queryFn: () => api.get<ResidentsOverview>('/residents/overview'),
  });

  const pages = useCursorPages();
  // Buscar volta para a primeira pagina: o cursor pertencia a lista anterior.
  const changeSearch = resetting(pages, setSearch);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['residents', 'list', debouncedSearch, pages.cursor],
    queryFn: () =>
      api.get<Page<Resident>>(
        `/residents?limit=50${
          debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : ''
        }${pages.cursor ? `&cursor=${encodeURIComponent(pages.cursor)}` : ''}`,
      ),
    placeholderData: keepPreviousData,
  });

  const items = data?.items ?? [];
  // Trocar de apartamento e o motivo mais comum de mexer num morador ja
  // cadastrado — e o vinculo e o que decide quem recebe a encomenda.
  const canEdit = allows('condominio:moradores:editar');
  const canImport =
    allows('condominio:moradores:criar') &&
    allows('condominio:moradores:editar') &&
    allows('condominio:unidades:criar');
  const whatsappShare =
    overview && overview.residents > 0
      ? Math.round((overview.whatsappConnected / overview.residents) * 100)
      : 0;

  return (
    <>
      <PageHeader title="Moradores" />

      {/* A importacao cria bloco e unidade junto — quem nao monta o predio
          tambem nao importa a lista dele. */}
      {importing && canImport && (
        <ImportResidentsCard onClose={() => setImporting(false)} />
      )}

      {allows('condominio:moradores:criar') && <NewResidentCard />}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Unidades cadastradas"
          value={overview?.units ?? '—'}
          hint="ativas na base"
        />
        <StatCard
          label="Moradores ativos"
          value={overview?.residents ?? '—'}
          hint="contatos cadastrados"
        />
        <StatCard
          label="WhatsApp conectado"
          value={overview?.whatsappConnected ?? '—'}
          hint={overview ? `${whatsappShare}% da base` : undefined}
          highlight
        />
        <StatCard
          label="Autorizacoes"
          value={overview?.authorizedPickups ?? '—'}
          hint="terceiros autorizados"
        />
      </div>

      <Card>
        <CardHeader
          title="Moradores"
          subtitle="Base para notificacoes e retiradas"
          action={
            // Importar deixou de ser recurso de plano: cadastrar 200 moradores
            // a mao nao acontece, e um produto vazio na primeira semana e o que
            // faz a conta nao decolar.
            canImport ? (
              <Button variant="ghost" onClick={() => setImporting(true)}>
                Importar lista
              </Button>
            ) : undefined
          }
        />

        <div className="px-5 pb-5 sm:px-6">
          <SearchInput
            value={search}
            onChange={(event) => changeSearch(event.target.value)}
            placeholder="Buscar por nome ou unidade..."
            aria-label="Buscar moradores"
          />
        </div>

        {isLoading && <LoadingRows rows={3} />}

        {!isLoading && items.length === 0 && (
          <EmptyState
            title="Nenhum morador encontrado"
            description="Cadastre ou importe a lista de moradores do condominio."
          />
        )}

        {items.length > 0 && (
          <>
            {/* Teto de altura + cabecalho grudado: com 50 linhas o rodape de
                paginacao saia da tela e a lista parecia acabar ali. */}
            <div className="hidden max-h-[60vh] overflow-auto border-t border-line-soft lg:block">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 z-10 bg-surface text-[0.7rem] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="px-6 py-3 font-semibold">Nome</th>
                    <th className="px-6 py-3 font-semibold">Unidade</th>
                    <th className="px-6 py-3 font-semibold">WhatsApp</th>
                    <th className="px-6 py-3 font-semibold">E-mail</th>
                    <th className="px-6 py-3 font-semibold">Pendentes</th>
                    <th className="px-6 py-3 font-semibold">Status</th>
                    {canEdit && <th className="px-6 py-3" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-line-soft">
                  {items.map((resident) => (
                    <Fragment key={resident.id}>
                      <tr className="hover:bg-surface-2/50">
                        <td className="px-6 py-4 font-bold">{resident.name}</td>
                        <td className="px-6 py-4">{describeUnits(resident)}</td>
                        <td className="px-6 py-4 text-muted">
                          <Contact resident={resident} />
                        </td>
                        <td className="max-w-[16rem] px-6 py-4 text-muted">
                          <Email resident={resident} />
                        </td>
                        <td className="px-6 py-4 font-bold tabular-nums">
                          {resident.pendingPackages}
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge tone={resident.status === 'active' ? 'accent' : 'neutral'}>
                            {resident.status === 'active' ? 'Ativo' : 'Inativo'}
                          </StatusBadge>
                        </td>
                        {canEdit && (
                          <td className="px-6 py-4 text-right">
                            <Button
                              variant="quiet"
                              className="py-1.5"
                              onClick={() =>
                                setEditing(editing === resident.id ? null : resident.id)
                              }
                            >
                              {editing === resident.id ? 'Fechar' : 'Editar'}
                            </Button>
                          </td>
                        )}
                      </tr>

                      {editing === resident.id && (
                        <tr>
                          <td colSpan={7} className="px-6 pb-5">
                            <ResidentDataEditor resident={resident} />
                            <ResidentUnitsEditor
                              resident={resident}
                              onDone={() => setEditing(null)}
                            />
                            <PrivacyActions resident={resident} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="space-y-3 border-t border-line-soft p-4 sm:p-5 lg:hidden">
              {items.map((resident) => (
                <li key={resident.id} className="rounded-2xl border border-line bg-surface-2 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-lg font-bold">{resident.name}</p>
                      <p className="text-sm text-muted">Unidade {describeUnits(resident)}</p>
                      <p className="text-sm text-muted">
                        <Contact resident={resident} />
                      </p>
                      <p className="truncate text-sm text-muted">
                        <Email resident={resident} />
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-bold">{resident.pendingPackages} pend.</p>
                      <div className="mt-2">
                        <StatusBadge tone={resident.status === 'active' ? 'accent' : 'neutral'}>
                          {resident.status === 'active' ? 'Ativo' : 'Inativo'}
                        </StatusBadge>
                      </div>
                    </div>
                  </div>

                  {canEdit && (
                    <>
                      <Button
                        variant="quiet"
                        className="mt-3 py-1.5"
                        onClick={() => setEditing(editing === resident.id ? null : resident.id)}
                      >
                        {editing === resident.id ? 'Fechar' : 'Editar'}
                      </Button>

                      {editing === resident.id && (
                        <div className="mt-3">
                          <ResidentDataEditor resident={resident} />
                          <ResidentUnitsEditor
                            resident={resident}
                            onDone={() => setEditing(null)}
                          />
                        </div>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>

            <Pagination pages={pages} nextCursor={data?.nextCursor ?? null} loading={isFetching} />
          </>
        )}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * Corrige o cadastro do morador.
 *
 * O telefone e o motivo de este bloco existir. Ate agora a tela deixava trocar
 * o APARTAMENTO do morador e nao o numero dele — e o numero e o que decide se o
 * aviso de encomenda chega. Um digito errado na planilha da administradora
 * viravam avisos que "saem" e nunca chegam, sem nada indicando que ha algo
 * errado.
 *
 * Manda so o que mudou: a rota e PATCH, campo ausente nao mexe em nada. Assim
 * duas pessoas corrigindo a mesma ficha em telas diferentes nao sobrescrevem
 * uma a outra por reenviar valores velhos.
 *
 * O documento fica de fora porque a listagem nao o devolve — editar as cegas um
 * campo que a tela nao mostra e como apagar no escuro.
 */
function ResidentDataEditor({ resident }: { resident: Resident }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(resident.name);
  const [phone, setPhone] = useState(maskPhone(resident.phoneE164 ?? ''));
  const [email, setEmail] = useState(resident.email ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const currentPhone = phoneToE164(phone);
  const dirty =
    name !== resident.name ||
    currentPhone !== (resident.phoneE164 ?? '') ||
    email !== (resident.email ?? '');

  const save = useMutation({
    mutationFn: () => {
      const payload: Record<string, string> = {};
      if (name !== resident.name) payload.name = name.trim();
      if (currentPhone !== (resident.phoneE164 ?? '')) payload.phoneE164 = currentPhone;
      if (email !== (resident.email ?? '')) payload.email = email.trim();
      return api.patch<void>(`/residents/${resident.id}`, payload);
    },
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['residents'] });
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Nao foi possivel salvar.'),
  });

  return (
    <div className="mt-3 rounded-2xl border border-line bg-surface-2 p-4">
      <p className="text-sm font-bold">Dados do morador</p>

      <div className="mt-3 grid gap-4 sm:grid-cols-3">
        <Field label="Nome">
          <TextInput
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setSaved(false);
            }}
            maxLength={120}
          />
        </Field>

        <Field label="WhatsApp">
          <MaskedInput
            mask={maskPhone}
            value={phone}
            onValueChange={(next) => {
              setPhone(next);
              setSaved(false);
            }}
            placeholder="(31) 9 9999-1122"
          />
        </Field>

        <Field label="E-mail">
          <TextInput
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setSaved(false);
            }}
            type="email"
            placeholder="Opcional"
            maxLength={160}
          />
        </Field>
      </div>

      {error && (
        <div className="mt-3">
          <Alert>{error}</Alert>
        </div>
      )}

      {/* Trocar o numero derruba a verificacao: o selo de "contato verificado"
          vale para o numero que respondeu, nao para a pessoa. Quem esta
          corrigindo precisa saber disso antes de salvar, nao depois. */}
      {resident.phoneVerifiedAt && currentPhone !== (resident.phoneE164 ?? '') && (
        <p className="mt-3 text-sm text-warn">
          Este contato estava verificado. Ao salvar o numero novo, a verificacao e desfeita ate o
          WhatsApp confirmar uma entrega.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          className="py-2"
          disabled={!dirty || name.trim().length < 2}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Salvar dados
        </Button>
        {saved && !dirty && <span className="text-sm text-accent">Salvo.</span>}
      </div>
    </div>
  );
}

/**
 * Cadastro de morador JA vinculado a uma unidade.
 *
 * O vinculo nasce junto de proposito: morador sem unidade fica pendurado no
 * condominio inteiro, nao aparece como destinatario de nada e o porteiro nao
 * consegue registrar encomenda para ele. Cadastrar agora e vincular depois era
 * um passo que ninguem lembrava de dar.
 */
function NewResidentCard() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [blockId, setBlockId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [relation, setRelation] = useState<ResidentRelation>('owner');
  const [isPrimary, setIsPrimary] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // O sindico cria unidade; o porteiro nao. Oferecer o atalho a quem vai
  // esbarrar em 403 seria pior do que nao oferecer nada.
  const canCreateUnits = useSession().allows('condominio:unidades:criar');

  const { data: units } = useQuery({
    queryKey: ['units', 'picker'],
    queryFn: () => api.get<Page<Unit>>('/units?limit=500'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/residents', {
        name,
        // `phoneToE164` e nao um `replace` de nao-digitos: o segundo gravava o
        // numero SEM o DDI, e e o DDI que o WhatsApp precisa para entregar.
        ...(phone ? { phoneE164: phoneToE164(phone) } : {}),
        // Campo ausente, e nao string vazia: a API valida `email` como e-mail
        // de verdade, e `""` seria recusado como invalido — transformando um
        // campo opcional deixado em branco num 422 no meio do cadastro.
        ...(email.trim() ? { email: email.trim() } : {}),
        units: unitId ? [{ unitId, relation, isPrimary }] : [],
      }),
    onSuccess: () => {
      setName('');
      setPhone('');
      setEmail('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['residents'] });
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Nao foi possivel cadastrar.'),
  });

  const unitItems = units?.items ?? [];

  // Blocos do condominio, e nao os que aparecem nas unidades: quem acabou de
  // criar a Torre A ainda nao tem apartamento nenhum nela — ver `useBlocks`.
  const blocks = useBlocks(unitItems);
  const hasBlocks = blocks.length > 0;
  const visibleUnits = blockId ? unitItems.filter((unit) => unit.blockId === blockId) : unitItems;

  return (
    <Card className="mb-4">
      <CardHeader
        title="Novo morador"
        subtitle="O vinculo com a unidade define quem recebe a encomenda."
      />

      <form
        // Com o e-mail sao 7 campos. Numa linha so eles ficariam com ~14% da
        // largura cada, e um endereco de e-mail nao cabe nisso — a partir do
        // `lg` o formulario quebra em duas linhas e so no `xl` volta a ser uma.
        className={cx(
          'grid gap-4 px-5 pb-5 sm:grid-cols-2 sm:px-6 lg:grid-cols-4',
          hasBlocks ? 'xl:grid-cols-7' : 'xl:grid-cols-6',
        )}
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        <Field label="Nome">
          <TextInput
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Gabriel Aguiar"
            maxLength={120}
          />
        </Field>

        <Field label="WhatsApp">
          <MaskedInput
            mask={maskPhone}
            value={phone}
            onValueChange={setPhone}
            placeholder="(31) 9 9999-1122"
          />
        </Field>

        {/* Opcional, como o telefone — mas os dois em branco significam um
            morador que o sistema nao consegue avisar de nada. */}
        <Field label="E-mail">
          <TextInput
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="morador@email.com"
            maxLength={160}
            autoComplete="off"
          />
        </Field>

        {/* Bloco antes de unidade, e nao dentro do nome dela: e assim que o
            sindico pensa o predio, e num condominio onde todo bloco tem um 101
            uma lista unica de "A • 101, B • 101" e onde se erra. */}
        {hasBlocks && (
          <Field label="Bloco">
            <Select
              value={blockId}
              onChange={(next) => {
                setBlockId(next);
                // A unidade escolhida pode nao pertencer ao novo bloco: some da
                // lista mas continuaria valendo no envio.
                if (next && !unitItems.some((u) => u.id === unitId && u.blockId === next)) {
                  setUnitId('');
                }
              }}
              emptyOption="Todos os blocos"
              options={blocks.map(([id, blockName]) => ({ value: id, label: blockName }))}
            />
          </Field>
        )}

        <Field label="Unidade">
          <Select
            value={unitId}
            onChange={setUnitId}
            emptyOption="Sem unidade"
            options={visibleUnits.map((unit) => ({
              value: unit.id,
              label: !blockId && unit.block ? `${unit.block} • ${unit.label}` : unit.label,
            }))}
          />
        </Field>

        <Field label="Vinculo">
          <Select
            value={relation}
            onChange={(next) => setRelation(next as ResidentRelation)}
            disabled={!unitId}
            options={Object.entries(RESIDENT_RELATION_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
          />
        </Field>

        <div className="flex flex-col justify-end gap-3">
          <label className="flex items-center gap-2 text-sm text-muted">
            <input
              type="checkbox"
              checked={isPrimary}
              disabled={!unitId}
              onChange={(event) => setIsPrimary(event.target.checked)}
              className="size-4 accent-accent"
            />
            Destinatario padrao
          </label>

          <Button type="submit" loading={create.isPending} disabled={name.trim().length < 2}>
            Cadastrar
          </Button>
        </div>
      </form>

      {error && (
        <div className="px-5 pb-5 sm:px-6">
          <Alert>{error}</Alert>
        </div>
      )}

      {/* Antes isto era so um texto citando o nome de outra tela — quem lia
          tinha que sair procurando no menu. O aviso agora leva ate la. */}
      {unitItems.length === 0 && (
        <div className="px-5 pb-5 sm:px-6">
          <div className="rounded-2xl border border-warn/30 bg-warn/10 p-4">
            <p className="text-sm font-semibold">
              Nenhuma unidade cadastrada ainda — e morador sem unidade nao recebe encomenda.
            </p>
            <p className="mt-1 text-sm text-muted">
              A ordem e: blocos (torres do predio) → unidades (os apartamentos) → moradores.
              O gerador cria o predio inteiro de uma vez.
            </p>
            {canCreateUnits && (
              <Link to="/unidades">
                <Button className="mt-4">Criar blocos e unidades</Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
